import { useEffect } from 'react'

export const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | RiverBlue'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for RiverBlue Revenue Protection. We do not sell, rent, or share personal information, including phone numbers, with third parties or affiliates for marketing or lead generation purposes.')
    } else {
      const meta = document.createElement('meta')
      meta.name = 'description'
      meta.content = 'Privacy Policy for RiverBlue Revenue Protection. We do not sell, rent, or share personal information, including phone numbers, with third parties or affiliates for marketing or lead generation purposes.'
      document.head.appendChild(meta)
    }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-12 md:py-16 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-500">
              Effective Date: January 6, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Introduction */}
            <div>
              <p className="text-gray-700 leading-relaxed">
                RiverBlue is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you use our services.
              </p>
            </div>

            {/* What We Collect */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                What We Collect
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect the following types of information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Work email address</strong> - Required for communication and service delivery</li>
                <li><strong>Company name</strong> - Required for service identification and support</li>
                <li><strong>HubSpot portal ID or domain</strong> (optional) - Used to access your HubSpot data for analysis</li>
                <li><strong>Internal employee/contractor phone numbers</strong> (if applicable) - Collected only from internal employees or approved contractors. Used exclusively for internal operational alerts and workflow notifications. SMS is used only for internal operational alerts and workflow notifications.</li>
              </ul>
            </div>

            {/* How We Use It */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the information we collect solely for internal operational purposes, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Service delivery and support</li>
                <li>System alerts and workflow notifications</li>
                <li>Internal operations and administration</li>
              </ul>
            </div>

            {/* Data Sharing & Sale of Personal Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Data Sharing & Sale of Personal Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                RiverBlue does not sell, rent, share, or disclose personal information, including phone numbers, to third parties or affiliates for marketing, advertising, or lead generation purposes.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Personal information may be processed only by authorized service providers (such as infrastructure, hosting, or messaging vendors) solely to operate and support our services. These providers are contractually restricted from using personal information for any marketing, advertising, or resale purposes.
              </p>
            </div>

            {/* No Selling/Sharing Statement */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8">
              <p className="text-gray-900 font-semibold text-lg leading-relaxed">
                <strong>We do not sell, rent, or share personal information, including phone numbers, with third parties or affiliates for marketing or lead generation purposes.</strong>
              </p>
            </div>

            {/* Data Retention */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Data Retention
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to provide our services and fulfill our operational purposes. You may request deletion of your data at any time by contacting us at <a href="mailto:hello@riverblue.io" className="text-blue-600 hover:text-blue-800 hover:underline">hello@riverblue.io</a>. We will delete your information where feasible and in accordance with applicable law.
              </p>
            </div>

            {/* Opt-Out */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Opt-Out
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                RiverBlue does not send marketing, promotional, or customer-facing SMS messages.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Internal users can opt out of SMS notifications at any time by replying <strong>STOP</strong> to any SMS message or by notifying an administrator. This applies to internal operational alerts only. Opt-out is available at any time.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights regarding your personal information, please contact us at:
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>Email:</strong>{' '}
                <a href="mailto:hello@riverblue.io" className="text-blue-600 hover:text-blue-800 hover:underline">
                  hello@riverblue.io
                </a>
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  )
}
